///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthBomReportService } from './bom-report.service';
import { BomSelectionFieldTypeEnum } from './bom-selection-field-type.enum';

import { GtthKeyValueDTO } from '../../../generated/shared-types.model';
import { _isEqual } from '../../../shared/utils';
import { SuiToggleControlComponent, SuiTypeToggle } from '@siklu/sui-lib/infrastructure';
import { FormsModule } from '@angular/forms';
import { ContextSensitiveHelpComponent } from '../../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { GtthStyleDirectivesModule } from '../../../shared/directives/style-directives/style-directives.module';

import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';

@Component({
    selector: 'winde-bom-selection-field',
    styleUrls: ['./bom-report.scss'],
    template: `
        @if (type === fieldType.COMBOBOX) {
            <div>
                @if (!historyService.isEditModeEnabled || !showEditComponent || readonly) {
                    @if (!isRadiosDisplay) {
                        <span class="d-block">{{ value }}</span>
                    } @else {
                        <div class="d-flex align-items-center">
                            <span class="d-block">{{ value }}</span>
                            <winde-ctx-sensitive-help placement="bottom" tooltipTextPath="productRadioName.btn.openProductPage">
                                <div class="ml-2 my-auto" [windeProductNameLink]="value" [isUseInfoButton]="true"></div>
                            </winde-ctx-sensitive-help>
                        </div>
                    }
                }
                @if (historyService.isEditModeEnabled && showEditComponent && !readonly) {
                    @if (!isRadiosDisplay) {
                        <select class="form-control form-control-sm" [ngModel]="valueId" (change)="onSelectionChange($event)">
                            @for (val of availableValues; track val) {
                                <option [value]="val.key">{{ val.value }}</option>
                            }
                        </select>
                    } @else {
                        <div class="d-flex align-items-center">
                            <select class="form-control form-control-sm" [ngModel]="valueId" (change)="onSelectionChange($event)">
                                @for (val of availableValues; track val) {
                                    <option [value]="val.key">{{ val.value }}</option>
                                }
                            </select>
                            <winde-ctx-sensitive-help placement="bottom" tooltipTextPath="productRadioName.btn.openProductPage">
                                <div class="ml-2 my-auto" [windeProductNameLink]="value" [isUseInfoButton]="true"></div>
                            </winde-ctx-sensitive-help>
                        </div>
                    }
                }
            </div>
        }

        @if (type === fieldType.CHECKBOX) {
            <div>
                @if (!showEditComponent) {
                    <span class="d-block">
                        {{ value }}
                    </span>
                }
                @if (showEditComponent && !historyService.isEditModeEnabled) {
                    <span class="d-block">
                        {{ checkBoxValue ? 'Enabled' : 'Disabled' }}
                    </span>
                }
                @if (showEditComponent && historyService.isEditModeEnabled) {
                    <span class="form-check-label">
                        <sui-toggle-component [config]="toggleConfig" (change)="onCheckboxChange()"
                            >{{ checkBoxValue ? 'Enabled' : 'Disabled' }}
                        </sui-toggle-component>
                    </span>
                }
            </div>
        }
    `,
    imports: [FormsModule, SuiToggleControlComponent, ContextSensitiveHelpComponent, GtthStyleDirectivesModule, ButtonsDirectivesModule]
})
export class GtthSelectionFieldComponent implements OnInit {
    fieldType = BomSelectionFieldTypeEnum;
    public showEditComponent = false;
    public checkBoxValue = false;
    public toggleConfig: SuiTypeToggle;
    @Input()
    public type: BomSelectionFieldTypeEnum;
    @Input()
    public valueId: number;
    @Input()
    public value: string;
    @Input()
    public readonly: boolean;
    @Input()
    public isRadiosDisplay = false;
    @Output()
    public selectedValue = new EventEmitter<GtthKeyValueDTO>();
    private afterInit = false;

    constructor(
        public bomReportService: GtthBomReportService,
        public historyService: GtthHistoryService
    ) {}

    private _availableValues: GtthKeyValueDTO[];

    get availableValues(): GtthKeyValueDTO[] {
        return this._availableValues;
    }

    @Input()
    set availableValues(value: GtthKeyValueDTO[]) {
        if (this.afterInit && !_isEqual(this._availableValues, value)) {
            this._availableValues = value;
            this.initBox();
        } else {
            this._availableValues = value;
        }
    }

    ngOnInit() {
        this.initBox();
    }

    onSelectionChange(event) {
        this.valueId = parseFloat(event.target.value);
        const valueObj = this._availableValues.find((value1) => value1.key === this.valueId);
        this.value = valueObj.value;
        this.selectedValue.emit(valueObj);
    }

    onCheckboxChange() {
        this.checkBoxValue = !this.checkBoxValue;
        const valueObj = this.checkBoxValue ? this._availableValues[1] : this._availableValues[0];
        this.valueId = valueObj.key;
        this.value = valueObj.value;
        this.selectedValue.emit(valueObj);
    }

    private initBox() {
        if (this.type === BomSelectionFieldTypeEnum.COMBOBOX) {
            this.initComboBox();
        } else {
            this.initCheckBox();
        }
    }

    private initComboBox() {
        if (this._availableValues.length === 1) {
            this.showEditComponent = false;
        } else {
            this.showEditComponent = true;
        }
        this.afterInit = true;
    }

    private initCheckBox() {
        if (this._availableValues.length < 2) {
            this.showEditComponent = false;
        } else {
            this.checkBoxValue = this.valueId === this._availableValues[1].key;
            this.showEditComponent = true;
        }
        this.afterInit = true;

        this.toggleConfig = new SuiTypeToggle(this.checkBoxValue);
        this.toggleConfig.applyModifiedStateStyle = false;
        this.toggleConfig.readonly = false;
    }
}
